import React from "react";
import { Star, ArrowRight, ShieldCheck, Clock, Heart, Award, Users, Leaf, Truck } from "lucide-react";
import { Fireflies } from "@/components/effects/Fireflies";
import { Reveal } from "@/components/effects/Reveal";
import { T, useSiteContent } from "@/context/SiteContent";

export const WhyUs = () => {
  const { get } = useSiteContent();
  // Values + labels for the big stats row are all admin-editable. Fallbacks
  // match the original hardcoded copy so a fresh install still looks right.
  const stats = [
    { value: get("stats.1.value", "5,000+"), label: get("stats.1.label", "Reservations delivered"), icon: Award, testid: "stat-reservations" },
    { value: get("stats.2.value", "500+"),   label: get("stats.2.label", "Five-star families"),      icon: Star, testid: "stat-reviews" },
    { value: get("stats.3.value", "24hr"),   label: get("stats.3.label", "Same-day booking"),        icon: Clock, testid: "stat-speed" },
    { value: get("stats.4.value", "100%"),   label: get("stats.4.label", "Sanitized between rentals"), icon: ShieldCheck, testid: "stat-clean" },
  ];
  // Six "why us" cards. Each card's title + body copy is admin-editable via
  // whyus.card{N}.title / whyus.card{N}.copy — keeps the array literal small.
  const reasons = [
    {
      icon: Award,
      title: get("whyus.card1.title", "5,000+ reservations of experience"),
      copy:  get("whyus.card1.copy",  "We're new to our own brand — but not to this magic. After 5,000+ successful family reservations on another platform, we're planting roots in the bayou and bringing every lesson with us."),
    },
    {
      icon: Heart,
      title: get("whyus.card2.title", "Family-owned, family-first"),
      copy:  get("whyus.card2.copy",  "You're not booking through a warehouse call center. Every drop-off, every crib linen, every text back — that's us. We know the difference a well-set-up crib makes at 9pm after a park day."),
    },
    {
      icon: ShieldCheck,
      title: get("whyus.card3.title", "Premium gear, hospital-grade clean"),
      copy:  get("whyus.card3.copy",  "UPPAbaby, Graco, and top-brand cribs — inspected and sanitized after every rental. Fresh laundered linens on every crib and pack 'n play, no exceptions."),
    },
    {
      icon: Truck,
      title: get("whyus.card4.title", "Bell services drop-off, contactless"),
      copy:  get("whyus.card4.copy",  "For Walt Disney World resorts we personally meet you in person. Bonnet Creek, Universal, Disney Springs Area hotels — we drop off at bell services so it's ready at check-in. Airbnbs and vacation homes — we meet you at the door."),
    },
    {
      icon: Users,
      title: get("whyus.card5.title", "We know Disney families"),
      copy:  get("whyus.card5.copy",  "Which strollers fit the monorail? Which cribs work in DVC studios? Which stroller handles the Boardwalk best? We've answered these thousands of times. Ask us anything."),
    },
    {
      icon: Leaf,
      title: get("whyus.card6.title", "Local, independent, rooted here"),
      copy:  get("whyus.card6.copy",  "We're an Orlando small business — no franchise, no middleman fees. Every dollar you spend goes back into fresher gear, cleaner sanitization, and more magical hand-offs."),
    },
  ];

  return (
    <section id="why-us" className="section relative">
      <div className="section-tint" />
      <Fireflies count={14} />
      <div className="container">
        {/* Intro */}
        <div className="max-w-3xl">
          <Reveal>
            <p className="eyebrow mb-4"><T k="whyus.eyebrow" fallback="Why Enchanted · A new chapter, same trusted hands" /></p>
            <h2 className="font-serif text-5xl md:text-6xl tracking-tight leading-none">
              <T k="whyus.headline" fallback="A fresh bayou-born brand, built on 5,000 magical trips." />
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 text-lg text-[#B9C8D5] leading-relaxed max-w-2xl">
              <T k="whyus.about_paragraph" fallback="Enchanted Gear Rentals is a new name — but not a new team. We've spent years delivering strollers and cribs to Disney families through another platform. Now we're planting roots in our own bayou, with the same premium gear, the same white-glove care, and every lesson from 5,000+ reservations baked in." />
            </p>
          </Reveal>
        </div>

        {/* Big stats row */}
        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <div className="glass p-6 md:p-8 text-center" data-testid={s.testid}>
                <s.icon className="w-6 h-6 mx-auto text-[#FFD166] mb-3" />
                <div className="font-serif text-4xl md:text-5xl text-[#FFD166] glow-warm leading-none">
                  {s.value}
                </div>
                <div className="mt-3 text-xs text-[#B9C8D5] uppercase tracking-[0.2em]">
                  {s.label}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Reasons grid */}
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reasons.map((r, i) => (
            <Reveal key={r.title} delay={(i % 3) * 0.08}>
              <div className="glass p-7 h-full hover:border-[#FFD166]/50 transition-colors" data-testid={`why-reason-${i}`}>
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#FFD166]/10 border border-[#FFD166]/30">
                  <r.icon className="w-5 h-5 text-[#FFD166]" />
                </div>
                <h3 className="font-serif text-2xl mt-5 leading-tight">{r.title}</h3>
                <p className="mt-3 text-[#B9C8D5] text-[15px] leading-relaxed">{r.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* CTA */}
        <Reveal delay={0.2}>
          <div className="mt-14 glass-warm p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6" data-testid="why-cta">
            <div>
              <p className="eyebrow mb-2">Ready when you are</p>
              <h3 className="font-serif text-3xl md:text-4xl leading-tight">
                Same magic. Fresh name. Reserve your gear tonight.
              </h3>
            </div>
            <a href="#trip-setup" className="shrink-0">
              <button className="btn-primary text-lg" data-testid="why-cta-book">
                Reserve Your Gear <ArrowRight className="inline w-5 h-5 ml-1" />
              </button>
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
